import { useEffect, useState } from 'react';
import { useOriginators } from '@/features/pipeline/useEntities';
import { useToast } from '@/components/ui/toast';
import { firstValidationMessage, type ApiError } from '@/lib/api/client';
import { usePersonDrawer } from './drawerStores';
import { usePersonProfile, useUpdatePersonProfile } from './profileApi';
import { InfoRow, ProfileDealsList, ProfileDrawerShell } from './drawerShared';

const INPUT = 'w-full rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink outline-none';
const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';

/** Drawer da ficha da Pessoa (DOCUMENTACAO.md §4.5): dados + deals como originadora externa + edição. */
export function PersonDrawer() {
  const id = usePersonDrawer((s) => s.id);
  const close = usePersonDrawer((s) => s.close);
  if (!id) return null;
  return <PersonDrawerInner id={id} onClose={close} />;
}

function PersonDrawerInner({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading, isError } = usePersonProfile(id);
  const originators = useOriginators();
  const update = useUpdatePersonProfile();
  const toast = useToast();
  const [editing, setEditing] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('');
  const [summary, setSummary] = useState('');
  const [originatorId, setOriginatorId] = useState('');

  useEffect(() => {
    if (data && editing) {
      setName(data.name);
      setEmail(data.email ?? '');
      setPhone(data.phone ?? '');
      setRole(data.role ?? '');
      setSummary(data.summary ?? '');
      setOriginatorId(data.currentOriginatorId ?? '');
    }
  }, [editing, data]);

  const originatorName = (oid: string | null): string =>
    oid ? (originators.data?.items.find((o) => o.id === oid)?.name ?? '—') : '—';

  const save = () => {
    if (!data || name.trim() === '') return;
    update.mutate(
      {
        id,
        eTag: data.eTag,
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        role: role.trim() || null,
        summary: summary.trim() || null,
        currentOriginatorId: originatorId || null,
      },
      {
        onSuccess: () => {
          toast('Pessoa atualizada');
          setEditing(false);
        },
        onError: (err) => {
          const status = (err as ApiError).status;
          toast(status === 412 ? 'Conflito: a ficha mudou. Reabra e tente de novo.' : (firstValidationMessage(err) ?? 'Erro ao salvar.'), 'danger');
        },
      },
    );
  };

  return (
    <ProfileDrawerShell
      kindLabel="Pessoa"
      title={data?.name ?? (isLoading ? 'Carregando…' : 'Pessoa')}
      subtitle={data?.role || undefined}
      onClose={onClose}
      onEdit={data ? () => setEditing(true) : undefined}
      editing={editing}
    >
      {isError ? (
        <div className="py-6 text-[13px] text-vega-danger">Erro ao carregar a ficha.</div>
      ) : !data ? (
        <div className="py-6 text-[13px] text-vega-muted">Carregando…</div>
      ) : editing ? (
        <div className="space-y-3">
          <div>
            <span className={LABEL}>Nome *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
          </div>
          <div>
            <span className={LABEL}>E-mail</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className={INPUT} />
          </div>
          <div>
            <span className={LABEL}>Telefone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className={INPUT} />
          </div>
          <div>
            <span className={LABEL}>Cargo</span>
            <input value={role} onChange={(e) => setRole(e.target.value)} className={INPUT} />
          </div>
          <div>
            <span className={LABEL}>Originadora atual</span>
            <select value={originatorId} onChange={(e) => setOriginatorId(e.target.value)} className={INPUT}>
              <option value="">—</option>
              {originators.data?.items.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div>
            <span className={LABEL}>Resumo</span>
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className={INPUT} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setEditing(false)} className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] text-vega-muted">
              Cancelar
            </button>
            <button
              type="button"
              onClick={save}
              disabled={update.isPending || name.trim() === ''}
              className="rounded bg-vega-ink px-3 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
            >
              {update.isPending ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-5">
            <InfoRow label="E-mail">{data.email || '—'}</InfoRow>
            <InfoRow label="Telefone">{data.phone || '—'}</InfoRow>
            <InfoRow label="Cargo">{data.role || '—'}</InfoRow>
            <InfoRow label="Originadora atual">{originatorName(data.currentOriginatorId)}</InfoRow>
            <InfoRow label="Resumo">{data.summary || '—'}</InfoRow>
          </div>
          <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-vega-muted">
            Deals em que foi originadora externa
          </h3>
          <ProfileDealsList deals={data.deals} />
        </>
      )}
    </ProfileDrawerShell>
  );
}
